/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MenuItem, ToolGroup, ToolItem } from '../models/tool-item-group';
import { RotateElementDirection } from '../models/rotate-element-direction';
import { SysquePopupConfig } from '../models/sysque-popup-config';
import { createDialog } from '../popup-dialog-factory/popup-dialog.facade';
import { Observable, Subscription } from 'rxjs';
import {
  IToolbeltFacade,
  TOOLBELT_FACADE_TOKEN,
} from '../../../../data-access/src/lib/domain/facades-models/toolbelt-facade-interface';
import { SysqueElectricalState } from '../models/sysque-electrical-state';
import { GlobalConstants } from '../constants/global-constants';
import { BooleanObject } from '../models/boolean-object';
import { ShiftSegmentReturnCodes } from '../../../../../shared/ui/src/lib/enums/shift-segment-return-codes';
import { RotateElementReturnCodes } from '../../../../../shared/ui/src/lib/enums/rotate-element-return-codes';
import { ShiftElementDirections } from '../../../../../shared/ui/src/lib/enums/shift-segment-directions';
import { AcrylicSurfaceService } from '../acrylic-surface/acrylic-surface.service';
import { ElectricalCategoryNames } from '../../../../../shared/ui/src/lib/enums/electrical-category-names';

@Component({
  selector: 'sysque-electrical-toolbelt',
  templateUrl: './electrical-toolbelt.component.html',
  styleUrls: ['./electrical-toolbelt.component.scss'],
  standalone: false
})
export class ElectricalToolbeltComponent implements OnInit {
  @Input() toolItems: ToolGroup[];
  @Output() processingEnabled = true;
  @Output() emitShiftElementReturnCode = new EventEmitter<any>();
  @Output() emitRotateElementReturnCode = new EventEmitter<any>();
  @Output() emitProcessEnable = new EventEmitter<any>();
  rotationValue: number;
  infoDialogVisible = false;
  itemName: string;
  processOnlyStatus: Subscription;
  processStatus$: Observable<BooleanObject>;
  dockedState = new SysqueElectricalState();
  selectedCategory: string = '';
  public showNonStandard = false;
  public processFittings = false;
  public selectedItem: ToolItem;
  public selectionExists = false;
  public isReportingDialog = false;
  public popUpYPosition = 0;
  private rotationAngle = 90;
  private closeMenuTimeoutId: any;

  constructor(
    @Inject(TOOLBELT_FACADE_TOKEN) private facade: IToolbeltFacade,
    private cdr: ChangeDetectorRef,
    private acrylicSurfaceService: AcrylicSurfaceService
  ) {
    this.emitRotationAction = this.emitRotationAction.bind(this);
    this.emitRotationValueChanged = this.emitRotationValueChanged.bind(this);
  }

  ngOnInit(): void {
    this.initializeProcessOnlyStatus();
    window.addEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    document.addEventListener('focusout', this.onFocusOut.bind(this), true);
    document.addEventListener('mousedown', this.onFocusOut.bind(this), true);
  }

  ngOnDestroy(): void {
    window.removeEventListener(GlobalConstants.STORAGE, this.handleStorageEvent.bind(this));
    document.removeEventListener('focusout', this.onFocusOut.bind(this), true);
    document.removeEventListener('mousedown', this.onFocusOut.bind(this), true);
  }

  public handleStorageEvent(event: StorageEvent): void {
    const result = JSON.parse(event.newValue);
    switch (event.key) {
      case GlobalConstants.SHOW_NON_STANDARD_RESPONSE:
        this.showNonStandard = result;
        this.cdr.detectChanges();
        break;
      case GlobalConstants.ROTATE_ELEMENT_RESPONSE:
        if (result !== RotateElementReturnCodes.Success) {
          this.emitRotateElementReturnCode.emit(result);
        }
        break;
      case GlobalConstants.SHIFT_SEGMENT_RESPONSE:
        if (result !== ShiftSegmentReturnCodes.Success) {
          this.emitShiftElementReturnCode.emit(result);
        }
        break;
      case GlobalConstants.ELEMENT_SELECTION_STATUS:
        this.selectionExists = event.newValue === 'true';
        if (!this.selectionExists) {
          this.resetSelection();
          this.selectedItem = null;
        }
        this.cdr.detectChanges();
        break;
      case GlobalConstants.SHOW_ACRYLIC_SURFACE:
        if (result === true) {
          this.acrylicSurfaceService.show();
        } else {
          this.acrylicSurfaceService.hide();
        }
        this.cdr.detectChanges();
        break;
      case GlobalConstants.UPDATE_PROCESS_FITTINGS_ONLY:
        if(result === 'true' || result === true){
          this.processFittings = true;
        }
        else {
          this.processFittings = false;
        }
        this.cdr.detectChanges();       
        this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
        this.selectedCategory = this.dockedState.selectedCategory;
        this.facade.updateOnlyFitting(this.selectedCategory, result as boolean);
        localStorage.removeItem(GlobalConstants.UPDATE_PROCESS_FITTINGS_ONLY);
        break;
      case GlobalConstants.UPDATE_SHOW_NON_STADARD:
        if (JSON.parse(event.newValue) === 'true') {
          this.showNonStandard = !this.showNonStandard;
          this.facade.showNonStandard(this.showNonStandard);
          this.cdr.detectChanges();
        }
        break;
      case GlobalConstants.ROTATE_ELEMENT:
        const direction = result as RotateElementDirection;
        this.facade.rotateElement(this.rotationAngle, direction);
        break;
      default:
        return;
    }
  }

  private initializeProcessOnlyStatus(): void {
    this.facade.updateLocalStorage(GlobalConstants.IS_SYSQUE_CONNECT_ON, false);
    this.facade.getProcessStatus();
    this.processStatus$ = this.facade.selectProcessFitting();
    this.processStatus$.subscribe((response) => {
      if (typeof response === 'boolean') {
        this.processFittings = response;
      } else {
        return;
      }
      this.dockedState.onlyFittingProcess = this.processFittings;
      this.facade.updateDispatchLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
    });
  }

  /**
   * Handle the action of the toggle changes
   * @param event The event emitted when the toggle value change
   */
  public onProcessToggleChange(event: any): void {
    if (event[0] === GlobalConstants.ProcessFittingsOnlyKey) {
      this.dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);
      this.processFittings = event[1];
      this.selectedCategory = this.dockedState.selectedCategory;
      this.dockedState.onlyFittingProcess = this.processFittings;
      this.facade.updateLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY, this.dockedState);
      // On toggle change, we want to write to the file the value that the user set, regardless of the selected category
      // By passing conduit to the backend side, we will ensure the value is written to the file
      this.facade.updateOnlyFitting(ElectricalCategoryNames.Conduit, event[1] as boolean);
    } else if (event[0] === GlobalConstants.ShowNonStandard && event[1] !== undefined) {
      this.facade.showNonStandard(event[1]);
      this.showNonStandard = event[1];
    } else if (event[0] === GlobalConstants.ProcessEnabled) {
      this.facade.processEnable(event[1]);
      this.processingEnabled = event[1];
      this.emitProcessEnable.emit(event[1]);
    }
  }

  /**
   * Handle the rotation action
   * @param value The value of the rotation angle
   * @param direction The direction of the rotation
   */
  public emitRotationAction(value: number, direction: RotateElementDirection): void {
    this.facade.rotateElement(value, direction);
  }

  /**
   * Handle the rotation value change
   * @param value The value of the rotation angle
   */
  public emitRotationValueChanged(value: number): void {
    this.rotationAngle = value;
  }

  /**
   * Handle the shift action
   * @param shiftDirection The shift direction
   */
  public emitShiftValue(shiftDirection: ShiftElementDirections): void {
    this.facade.shiftElement(shiftDirection);
  }

  private toggleNestedMenu(item: ToolItem): void {
    this.resetSelection();
    if (this.selectedItem === item) {
      this.selectedItem = null;
    } else {
      this.selectedItem = item;
      item.isSelected = true;
    }
  }

    public onFocusOut(event: FocusEvent | MouseEvent): void {
    // Delay closing to allow click events to register
    this.closeMenuTimeoutId = setTimeout(() => {
      if (!event.relatedTarget || !(event.relatedTarget as HTMLElement).closest('.pop-up-menu')) {
        this.resetSelection();
        this.selectedItem = null;
      }
    }, 150); // 150ms delay
  }

  public resetSelection(): void {
    if (this.toolItems) {
      this.toolItems.forEach((tool) => {
        if (tool.items) {
          tool.items.forEach((item) => (item.isSelected = false));
        }
      });
    }
  }

  public showSysquePopup(item: ToolItem, element?: HTMLElement): void {
    // Cancel any pending close menu timeout to allow click to process
    if (this.closeMenuTimeoutId) {
      clearTimeout(this.closeMenuTimeoutId);
      this.closeMenuTimeoutId = null;
    }
    const dialogConstants = [
      GlobalConstants.FIELDFAB_MARKS_DIALOG,
      GlobalConstants.CONDUITRUN_EDIT_DIALOG,
      GlobalConstants.ALIGN_COUPLINGS_DIALOG,
      GlobalConstants.OBJECT_UNITE_DIALOG,
      GlobalConstants.SPECIFICATION_SETTINGS_DIALOG,
      GlobalConstants.CONFIGURATION_EXPORT_DIALOG,
      GlobalConstants.CONDUIT_REPORT_DIALOG,
      GlobalConstants.CABLETRAY_REPORT_DIALOG,
      GlobalConstants.PANELCIRCUIT_REPORT_DIALOG,
      GlobalConstants.APPLY_SYSTEM_DATA,
      GlobalConstants.APPLY_NOMINAL_SIZE,
      GlobalConstants.UPDATE_EXTENDED_DATA,
    ];
    const dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    if (this.isItemDisabled(item)) {
      return;
    }

    if (item.items !== undefined) {
      this.toggleNestedMenu(item);
    }

    if (item.itemName === GlobalConstants.DirectTakeOff) {
      this.facade.updateLocalStorage(GlobalConstants.IS_SYSQUE_CONNECT_ON, true);
    }

    if (dialogConstants.includes(item.itemName)) {
      this.facade.sendPopupRoutingWpfDialogs(item.itemName, dockedState.selectedSystem);
      // This line currently covers WPF dialogues as well, but there is no Close in the BE for WPF dialogues
      this.acrylicSurfaceService.show();
      return;
    }

    if (
      item.itemName === GlobalConstants.CheckForExceedingFill ||
      item.itemName === GlobalConstants.CheckForExceedingMaxAngle ||
      item.itemName === GlobalConstants.DirectTakeOff ||
      item.itemName === GlobalConstants.ApplyPanelCircuitData ||
      item.itemName === GlobalConstants.GO_TO_SYSTEM ||
      item.itemName === GlobalConstants.ConvertSystem ||
      item.itemName === GlobalConstants.PlaceCoupling
    ) {
      this.facade.sendPopupRoutingNoDialogs(item.itemName, dockedState.selectedSystem, dockedState.selectedCategory);
      if (
        item.itemName === GlobalConstants.DirectTakeOff ||
        item.itemName === GlobalConstants.ConvertSystem ||
        item.itemName === GlobalConstants.PlaceCoupling
      ) {
        this.acrylicSurfaceService.show();
      }

      if (item.itemName === GlobalConstants.ApplyPanelCircuitData) {
        this.facade.updateLocalStorage(GlobalConstants.SHOW_ACRYLIC_SURFACE, false);
      }
      return;
    }

    this.isReportingDialog = item.itemName === GlobalConstants.Reporting;
    this.itemName = item.itemName;
    let sysquePopupConfig = new SysquePopupConfig();
    createDialog(sysquePopupConfig, item.itemName);
    if (
      item.itemName !== GlobalConstants.AddDataParameters &&
      !this.isReportingDialog &&
      item.itemName !== GlobalConstants.CodeChecking
    ) {
      this.acrylicSurfaceService.show();
    }
    if (item.items == undefined || item.items.length == 0) {
      this.facade.sendPopupRoutingAction(item.itemName, sysquePopupConfig.width, sysquePopupConfig.height);
      this.resetSelection();
    }

    if (item.itemName === GlobalConstants.DownloadMissingFamiliesToolbelt) {
      this.facade.updateDispatchLocalStorage(GlobalConstants.DOWNLOAD_MISSING_FAMILIES, 'true');
    }

    if (item.itemName === GlobalConstants.UpdateFamilies) {
      this.facade.updateDispatchLocalStorage(GlobalConstants.UPDATE_FAMILIES, 'true');
    }
    const itemRect = element.getBoundingClientRect();
    const browserHeight = window.innerHeight;
    this.cdr.detectChanges();
    const nestedMenu = element.querySelector('.pop-up-menu') as HTMLElement;

    if (nestedMenu) {
      const nestedMenuoffsetHeight = nestedMenu.offsetHeight;
      const menuPosition = itemRect.top;
      const availableSpaceBelow = browserHeight - menuPosition;
      if (availableSpaceBelow < nestedMenuoffsetHeight) {
        this.popUpYPosition = browserHeight - nestedMenuoffsetHeight;
      } else {
        this.popUpYPosition = itemRect.top;
      }
    }
  }

  public isItemDisabled(item: MenuItem): boolean {
    let dockedState = this.facade.getLocalStorage(GlobalConstants.SYSQUE_ELECTRICAL_STATE_KEY);

    if (
      [
        GlobalConstants.ConvertSystem,
        GlobalConstants.PlaceCoupling,
        GlobalConstants.ApplyEquipmentDetails,
        GlobalConstants.ApplySystemData,
        GlobalConstants.ApplyNominalSize,
        GlobalConstants.Reporting,
        GlobalConstants.DirectTakeOff,
        GlobalConstants.AddDataParameters,
        GlobalConstants.CodeChecking,
        GlobalConstants.GO_TO_SYSTEM,
      ].includes(item.itemName)
    ) {
      let shouldBeDisabled = true;

      if (
        this.selectionExists &&
        ([
          GlobalConstants.ApplyEquipmentDetails,
          GlobalConstants.AddDataParameters,
          GlobalConstants.CodeChecking,
          GlobalConstants.Reporting,
          GlobalConstants.GO_TO_SYSTEM,
        ].includes(item.itemName) ||
          dockedState.selectedSystem)
      ) {
        shouldBeDisabled = false;
      }
      if (item.itemName === GlobalConstants.DirectTakeOff && dockedState.selectedSystem) {
        shouldBeDisabled = !this.facade.getLocalStorage(GlobalConstants.IS_DIRECT_TAKEOFF_ENABLED);
      }
      item.disabled = shouldBeDisabled;
      return shouldBeDisabled;
    }

    return false;
  }
}
